import {
  createContext,
  FC,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useReducer,
} from 'react'
import Snackbar from '@mui/material/Snackbar'
import Alert, { AlertColor } from '@mui/material/Alert'
import Typography from '@mui/material/Typography'

interface Toast {
  id: string
  type: AlertColor
  message: string | ReactNode
}

interface IToastContext {
  createToast: (e: Toast) => void
}

const defaultValue = {
  createToast: () => {},
}

export const ToastContext = createContext<IToastContext>(defaultValue)

type Action =
  | { type: 'ADD_TOAST'; payload: Toast }
  | { type: 'REMOVE_TOAST'; payload: { id: string } }

const reducer = (state: Record<string, Toast>, action: Action) => {
  switch (action.type) {
    case 'ADD_TOAST':
      return { ...state, [action.payload.id]: action.payload }
    case 'REMOVE_TOAST':
      const updated = { ...state }
      delete updated[action.payload.id]
      return { ...updated }
    default:
      return state
  }
}

const ToastManager: FC<PropsWithChildren> = ({ children }) => {
  const [toasts, dispatch] = useReducer(reducer, {})

  const handleClose = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_TOAST', payload: { id } })
  }, [])

  const createToast = useCallback((e: Toast) => {
    dispatch({ type: 'ADD_TOAST', payload: e })
  }, [])

  const toastArray = Object.values(toasts)
  return (
    <ToastContext.Provider
      value={{
        createToast,
      }}
    >
      {toastArray.map(({ id, message, type }, index) => (
        <Snackbar
          key={id}
          open={true}
          autoHideDuration={5000}
          onClose={() => {
            handleClose(id)
          }}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ mt: index * 8 }}
        >
          <Alert
            severity={type}
            color={type}
            sx={{ width: { xs: '98vw', sm: '100%' }, alignItems: 'center' }}
            onClose={(e) => {
              handleClose(id)
              e.preventDefault()
              e.stopPropagation()
            }}
          >
            <Typography variant={'h5'} component={'p'} fontWeight={400}>
              {message}
            </Typography>
          </Alert>
        </Snackbar>
      ))}

      {children}
    </ToastContext.Provider>
  )
}

export default ToastManager
